var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { atom, useAtom, useSetAtom } from 'jotai';
import { atomForLocal } from '@/atoms/atomForLocal';
import useAppComponents from '@/hooks/useAppComponents';
import Logger from '@/logging/Logger';
import { logDebug } from '@/utils/DebugUtils';
import { useEffect } from 'react';
const userInfoAtom = atomForLocal('userInfo');
export const ANONYMOUS_USER_TEMPLATE = {
    id: '0',
    nickname: '비회원',
    mobilityTools: [],
    isNewsLetterSubscriptionAgreed: false,
    isMarketingPushAgreed: false,
    interestedRegionIds: [],
    interestedThemes: [],
};
// 비회원(익명) 판별의 단일 소스. nickname이 '비회원'이거나 레거시 id '0'이면 익명.
// atom(React)과 RootScreen의 storage 기반 게이트가 이 predicate를 공유한다.
export function isAnonymousUser(userInfo) {
    return ((userInfo === null || userInfo === void 0 ? void 0 : userInfo.id) === '0' ||
        (userInfo === null || userInfo === void 0 ? void 0 : userInfo.nickname) === ANONYMOUS_USER_TEMPLATE.nickname);
}
export const isAnonymousUserAtom = atom(get => isAnonymousUser(get(userInfoAtom)));
export const accessTokenAtom = atomForLocal('scc-token');
export const featureFlagAtom = atom(null);
// Key: 실험 이름 (e.g., 'UPVOTE_BUTTON_STYLE')
// Value: { variant: 배정된 variant, availableVariants: 가능한 variant 목록 }
export const experimentAtom = atom(null);
// DevTool용 로컬 오버라이드 (앱 재시작 시 초기화됨, DB 저장 안 함)
export const experimentOverrideAtom = atom({});
// useMe hook - centralized userInfo management
export function useMe() {
    const { api } = useAppComponents();
    const [userInfo, _setUserInfoAtom] = useAtom(userInfoAtom);
    const setFeatureFlag = useSetAtom(featureFlagAtom);
    const setExperiment = useSetAtom(experimentAtom);
    const _syncUserInfo = (newUserInfo) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        logDebug('syncUserInfo', newUserInfo);
        // 레거시; 과거에 비회원 유저의 id를 '0'으로 저장하던 시기를 처리하기 위한 로직.
        if ((newUserInfo === null || newUserInfo === void 0 ? void 0 : newUserInfo.id) === '0') {
            _setUserInfoAtom(ANONYMOUS_USER_TEMPLATE);
            setExperiment(null);
            return;
        }
        // 이 함수의 목적은 userInfo.id를 기반으로 userInfo를 갱신하는 것이므로,
        // userInfo 혹은 userInfo.id가 null인 경우는 sync를 스킵한다.
        if ((newUserInfo === null || newUserInfo === void 0 ? void 0 : newUserInfo.id) == null) {
            setExperiment(null);
            return;
        }
        // 비회원 유저이더라도 userInfo.id는 올바르게 채번되어 있으므로 Logger에 userId를 세팅해준다.
        yield Logger.setUserId(newUserInfo === null || newUserInfo === void 0 ? void 0 : newUserInfo.id);
        // 이후 작업은 IDENTIFIED 유저에 대해서만 진행한다.
        if ((newUserInfo === null || newUserInfo === void 0 ? void 0 : newUserInfo.nickname) === ANONYMOUS_USER_TEMPLATE.nickname) {
            setExperiment(null);
            return;
        }
        // 이미 저장된 유저 정보가 있으면 상태 업데이트
        // 메인화면 진입 시마다 새로 유저 정보를 업데이트한다.
        const { data } = yield api.getUserInfoGet();
        _setUserInfoAtom(data.user);
        setFeatureFlag({
            isAlbumUploadAllowed: (_a = data.isAlbumUploadAllowed) !== null && _a !== void 0 ? _a : false,
            hasBeenCrew: (_b = data.hasBeenCrew) !== null && _b !== void 0 ? _b : false,
            enabledFlags: new Set((_c = data.flags) !== null && _c !== void 0 ? _c : []),
        });
        // 실험 배정 파싱
        const experimentsMap = {};
        (_d = data.experiments) === null || _d === void 0 ? void 0 : _d.forEach(exp => {
            var _a, _b, _c;
            if (((_a = exp.experiment) === null || _a === void 0 ? void 0 : _a.name) && exp.variant) {
                experimentsMap[exp.experiment.name] = {
                    variant: exp.variant,
                    availableVariants: (_c = (_b = exp.experiment.variants) === null || _b === void 0 ? void 0 : _b.map(v => v.name)) !== null && _c !== void 0 ? _c : [],
                };
            }
        });
        setExperiment(experimentsMap);
    });
    const setUserInfo = (user) => __awaiter(this, void 0, void 0, function* () {
        _setUserInfoAtom(user);
        // fire-and-forget: 401 등은 axios interceptor 가 토큰 정리 + 로그인 리다이렉트로
        // 처리하므로 여기서 unhandled rejection 으로 새지 않게 catch 한다.
        _syncUserInfo(user).catch(e => logDebug('syncUserInfo failed', e));
    });
    const syncUserInfo = () => __awaiter(this, void 0, void 0, function* () {
        userInfo && (yield _syncUserInfo(userInfo));
    });
    // Auto-sync when userInfo is loaded from storage
    useEffect(() => {
        if (userInfo &&
            userInfo.id &&
            userInfo.nickname !== ANONYMOUS_USER_TEMPLATE.nickname) {
            // catch: 만료/로그아웃 후 getUserInfo 401 은 interceptor 가 처리하므로
            // unhandled rejection 으로 새지 않게 한다 (웹 dev 오버레이 블락 방지).
            _syncUserInfo(userInfo).catch(e => logDebug('syncUserInfo failed', e));
        }
    }, [userInfo === null || userInfo === void 0 ? void 0 : userInfo.id]);
    return { userInfo, setUserInfo, syncUserInfo };
}
